import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, type Resolver, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CSOSN_ICMS,
  CST_ICMS,
  CST_IPI,
  CST_PIS_COFINS,
  novoItemTributoFormSchema,
  type ConverterExcecaoResponse,
  type ItemFormValores,
  type ItemTributoResponse,
  type NovoItemTributoDto,
  type NovoItemTributoFormEntrada,
} from '@triade/contracts';
import { useNovoTributo, useTributos } from '@/api/itens-tributos';
import { useModoTabelaDoItem } from '@/api/itens-tributos-excecao';
import { useConverterExcecao } from '@/api/itens-tributos-colunas-antigas';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { formatarContagem, formatarData, formatarTaxa, fracaoParaPercentualTexto } from '@/lib/format';
import { MensagemCampo } from './dados-item';
import { apenasDigitos, GRUPOS, mascaraFiscal } from './mascaras-fiscais';
import { CamposExcecaoTributos, RegrasEstruturadas } from './tributos-excecao-campos';
import { CAMPOS_EXCECAO_VAZIOS, espelhoLegado, excecaoParaCorpo, textoUsaTabela, valoresExcecao, type FormVigencia, type ModoTabelaDoItem } from './tributos-excecao';
import { colunasAntigasPendentes, type CampoColunaAntiga } from './tributos-colunas-antigas';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const CAMPO_SELECT = 'h-10 w-full rounded-[10px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none aria-[invalid=true]:border-destructive';
/** Aviso fixo da aba (Sessao 6, item 7): o cadastro fiscal nao entra em calculo. Texto exato pedido pelo Pedro. */
export const AVISO_REFORMA = 'Cadastro apenas — cálculo da reforma tributária congelado';

const CAMPOS_TAXA = [
  { campo: 'pis', rotulo: 'PIS' },
  { campo: 'cofins', rotulo: 'COFINS' },
  { campo: 'icms', rotulo: 'ICMS' },
  { campo: 'iss', rotulo: 'ISS' },
  { campo: 'ipi', rotulo: 'IPI' },
] as const;

const OPCAO_VAZIA = '—';

/** Campo legado de cada tributo (pis..ipi) e o campo de excecao correspondente no modo estruturado. */
const EXCECAO_DO_CAMPO = { pis: 'excPis', cofins: 'excCofins', icms: 'excIcms', iss: 'excIss', ipi: 'excIpi' } as const;

const NATUREZA_TEXTO: Record<string, string> = { BEM: 'bem', SERVICO: 'serviço' };
const REGIME_TEXTO: Record<string, string> = { NORMAL: 'normal', MONOFASICO: 'monofásico', ALIQUOTA_ZERO: 'alíquota zero' };

export function hojeIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Valores iniciais do formulario "nova vigencia": a vigente em percentual (texto) e o cadastro fiscal com a mascara, para editar so o que muda. */
export function valoresVigencia(v: ItemTributoResponse | null | undefined, data: string = hojeIso()): NovoItemTributoFormEntrada {
  return {
    validFrom: data,
    pis: fracaoParaPercentualTexto(v?.pis),
    cofins: fracaoParaPercentualTexto(v?.cofins),
    icms: fracaoParaPercentualTexto(v?.icms),
    iss: fracaoParaPercentualTexto(v?.iss),
    ipi: fracaoParaPercentualTexto(v?.ipi),
    cstIcms: v?.cstIcms ?? '',
    motivo: '',
    ncm: mascaraFiscal(v?.ncm ?? '', GRUPOS.ncm),
    cest: mascaraFiscal(v?.cest ?? '', GRUPOS.cest),
    cstPis: v?.cstPis ?? '',
    cstCofins: v?.cstCofins ?? '',
    cstIpi: v?.cstIpi ?? '',
    icmsSt: v?.icmsSt ?? false,
    nbs: mascaraFiscal(v?.nbs ?? '', GRUPOS.nbs),
    cstIbsCbs: v?.cstIbsCbs ?? '',
    cClassTrib: v?.cClassTrib ?? '',
    reducaoIbs: fracaoParaPercentualTexto(v?.reducaoIbs),
    reducaoCbs: fracaoParaPercentualTexto(v?.reducaoCbs),
    impostoSeletivo: v?.impostoSeletivo ?? false,
    aliquotaIs: fracaoParaPercentualTexto(v?.aliquotaIs),
  };
}

/** Sessao 51 (U2): no modo estruturado as taxas legadas nao sao digitadas; o formulario parte das excecoes do item (vazio = usa a tabela). */
export function valoresVigenciaEstruturada(v: ItemTributoResponse | null | undefined, data: string = hojeIso()): FormVigencia {
  return { ...valoresVigencia(v, data), pis: '', cofins: '', icms: '', iss: '', ipi: '', ...valoresExcecao(v) };
}

function valoresDoFormulario(v: ItemTributoResponse | null | undefined, estruturada: boolean, data: string = hojeIso()): FormVigencia {
  return estruturada ? valoresVigenciaEstruturada(v, data) : { ...valoresVigencia(v, data), ...CAMPOS_EXCECAO_VAZIOS };
}

export interface TributosItemProps {
  itemId: string;
  /** Formulario do item: o ZFM e atributo do item (sem vigencia) e salva junto com ele; o tipo decide se aparece NBS. */
  form: UseFormReturn<ItemFormValores, unknown, ItemFormValores>;
  /** Vigente hoje, resolvido pela API (GET /itens/:id/resumo). */
  vigente: ItemTributoResponse | null | undefined;
  /** Informa a tela quando ha uma vigencia digitada e nao registrada (para o aviso de saida). */
  onDirtyChange?: (dirty: boolean) => void;
  /** Pode registrar vigencia (tributo.editar). Sem a permissao a tela so mostra o vigente e o historico. Padrao: sim. */
  podeRegistrar?: boolean;
}

type Vig = UseFormReturn<FormVigencia, unknown, NovoItemTributoDto>;

/**
 * Aba "Tributacao": vigente, ZFM, formulario de nova vigencia (aliquotas e cadastro fiscal) e historico. Uma nova vigencia
 * fecha a anterior na API (POST /itens/:id/tributos); nada aqui altera uma vigencia existente. Aliquotas entram em % e viram
 * fracao pelo contrato. O cadastro fiscal e so cadastro: a validacao e do contrato (mensagens em pt-BR).
 * Sessao 51 (U2): com a tabela padrao em modo ESTRUTURADO, PIS/COFINS/ICMS/ISS/IPI viram excecoes do item (vazio = usa a tabela)
 * e aparecem as regras novas; em LEGADO (ou sem tabela estruturada) a tela e a de sempre.
 */
export function TributosItem({ itemId, form, vigente, onDirtyChange, podeRegistrar = true }: TributosItemProps) {
  const historico = useTributos(itemId);
  const registrar = useNovoTributo();
  const modoTabela: ModoTabelaDoItem = useModoTabelaDoItem(hojeIso());
  const estruturada = modoTabela.modo === 'ESTRUTURADO';
  const tipo = form.watch('tipo');
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);

  const padrao = useMemo(() => valoresDoFormulario(vigente, estruturada), [vigente, estruturada]);
  const vig = useForm<FormVigencia, unknown, NovoItemTributoDto>({
    // O contrato valida so os campos dele; os de excecao (Sessao 51) sao validados em enviar (excecaoParaCorpo).
    resolver: zodResolver(novoItemTributoFormSchema) as unknown as Resolver<FormVigencia, unknown, NovoItemTributoDto>,
    defaultValues: padrao,
    mode: 'onTouched',
  });
  const { errors, isDirty } = vig.formState;

  // A vigente mudou no servidor (registrou, outra aba) ou o modo da tabela ficou conhecido: o formulario parte dela, se nao estiver sendo editado.
  useEffect(() => {
    if (!vig.formState.isDirty) vig.reset(padrao);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [padrao]);
  useEffect(() => onDirtyChange?.(isDirty), [isDirty, onDirtyChange]);

  async function enviar(dto: NovoItemTributoDto) {
    setErro(null);
    setOk(null);
    // NBS so existe para SERVICO (a API recusa NBS_SO_SERVICO): fora dele o campo nao vai no corpo.
    const { nbs, ...resto } = dto;
    let corpo: Omit<NovoItemTributoDto, 'nbs'> = resto;
    if (estruturada) {
      const excecao = excecaoParaCorpo(vig.getValues());
      if (!excecao.ok) {
        for (const [campo, mensagem] of Object.entries(excecao.erros)) vig.setError(campo as keyof FormVigencia, { message: mensagem });
        return;
      }
      corpo = { ...resto, ...espelhoLegado(excecao.corpo), ...excecao.corpo };
    }
    try {
      const nova = await registrar.mutateAsync({ itemId, ...corpo, ...(tipo === 'SERVICO' && nbs ? { nbs } : {}) });
      setOk(`Vigência registrada a partir de ${formatarData(nova.validFrom)}. A anterior foi encerrada nessa data.`);
      vig.reset(valoresDoFormulario(nova, estruturada));
    } catch (e) {
      setErro(e);
      if (e instanceof ApiError && e.status === 400) {
        for (const i of e.body?.issues ?? []) {
          const campo = i.path.split('.')[0] as keyof FormVigencia;
          if (campo in padrao) vig.setError(campo, { message: i.message });
        }
      }
    }
  }

  return (
    <div className="flex flex-col gap-5 p-5">
      <div>
        <h3 className="text-body font-semibold">Vigente hoje</h3>
        {vigente === undefined ? (
          <p role="status" className="mt-2 text-small text-ink-muted">
            Carregando tributos…
          </p>
        ) : vigente === null ? (
          <p role="status" className="mt-2 text-small text-ink-muted">
            Sem tributos vigentes hoje. Sem eles o item não pode ser precificado: registre uma vigência abaixo (pode ser zerada).
          </p>
        ) : (
          <>
            <dl data-testid="tributos-vigentes" className="mt-2 grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-3">
              {estruturada
                ? CAMPOS_TAXA.map(({ campo, rotulo }) => {
                    const excecao = vigente[EXCECAO_DO_CAMPO[campo]];
                    return (
                      <div key={campo} className="rounded-xl bg-surface-muted px-3 py-2.5">
                        <dt className="text-caption text-ink-muted">{rotulo}</dt>
                        <dd className="tabular text-metric font-semibold">
                          {excecao !== null && excecao !== undefined ? (
                            <>
                              {formatarTaxa(excecao)} <span className="rounded-full bg-primary/10 px-2 py-0.5 text-caption font-semibold text-primary">exceção</span>
                            </>
                          ) : (
                            <span className="text-small font-normal text-ink-secondary">{textoUsaTabela(rotulo, modoTabela.perfil)}</span>
                          )}
                        </dd>
                      </div>
                    );
                  })
                : CAMPOS_TAXA.map(({ campo, rotulo }) => (
                    <div key={campo} className="rounded-xl bg-surface-muted px-3 py-2.5">
                      <dt className="text-caption text-ink-muted">{rotulo}</dt>
                      <dd className="tabular text-metric font-semibold">{formatarTaxa(vigente[campo])}</dd>
                    </div>
                  ))}
              <div className="rounded-xl bg-surface-muted px-3 py-2.5">
                <dt className="text-caption text-ink-muted">CST ICMS</dt>
                <dd className="tabular text-metric font-semibold">{vigente.cstIcms ?? '—'}</dd>
              </div>
              <div className="col-span-full text-caption text-ink-muted">
                Desde {formatarData(vigente.validFrom)}
                {vigente.motivo ? ` · ${vigente.motivo}` : ''}
              </div>
            </dl>
            <dl data-testid="cadastro-fiscal-vigente" className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-x-3 gap-y-2 text-small">
              <Fiscal rotulo="NCM" valor={vigente.ncm ? mascaraFiscal(vigente.ncm, GRUPOS.ncm) : null} />
              <Fiscal rotulo="CEST" valor={vigente.cest ? mascaraFiscal(vigente.cest, GRUPOS.cest) : null} />
              <Fiscal rotulo="CST PIS" valor={vigente.cstPis ?? null} />
              <Fiscal rotulo="CST COFINS" valor={vigente.cstCofins ?? null} />
              <Fiscal rotulo="CST IPI" valor={vigente.cstIpi ?? null} />
              <Fiscal rotulo="ICMS-ST" valor={vigente.icmsSt === undefined ? null : vigente.icmsSt ? 'sim' : 'não'} />
              <Fiscal rotulo="NBS" valor={vigente.nbs ? mascaraFiscal(vigente.nbs, GRUPOS.nbs) : null} />
              <Fiscal rotulo="CST IBS/CBS" valor={vigente.cstIbsCbs ?? null} />
              <Fiscal rotulo="cClassTrib" valor={vigente.cClassTrib ?? null} />
              <Fiscal rotulo="Imposto Seletivo" valor={vigente.impostoSeletivo === undefined ? null : vigente.impostoSeletivo ? `sim, ${formatarTaxa(vigente.aliquotaIs)}` : 'não'} />
              {estruturada && (
                <>
                  <Fiscal rotulo="Importado (ICMS 4%)" valor={vigente.importadoIcms4 ? 'sim' : 'não'} />
                  <Fiscal rotulo="Redução da base do ICMS" valor={vigente.reducaoBaseIcms ? formatarTaxa(vigente.reducaoBaseIcms) : null} />
                  <Fiscal rotulo="Natureza (presunção)" valor={NATUREZA_TEXTO[vigente.naturezaPresuncao ?? ''] ?? null} />
                  <Fiscal rotulo="Regime PIS/COFINS" valor={REGIME_TEXTO[vigente.pisCofinsRegime ?? ''] ?? null} />
                  <Fiscal rotulo="MVA da ST" valor={vigente.stMva ? formatarTaxa(vigente.stMva) : null} />
                  <Fiscal rotulo="Alíquota da ST" valor={vigente.stAliquota ? formatarTaxa(vigente.stAliquota) : null} />
                </>
              )}
            </dl>
          </>
        )}
      </div>

      <label htmlFor="i-zfm" className="flex w-fit cursor-pointer items-center gap-2">
        <input id="i-zfm" type="checkbox" className="size-[18px] accent-primary" {...form.register('zfm')} />
        <span>
          Zona Franca de Manaus (ZFM)
          <span className="block text-caption text-ink-muted">Atributo do item inteiro, sem vigência. Salva com o item (Salvar ⌘S).</span>
        </span>
      </label>

      {estruturada && <AvisoColunasAntigas itemId={itemId} vigente={vigente} podeConverter={podeRegistrar} />}

      {podeRegistrar && modoTabela.modo === 'CARREGANDO' && (
        <p role="status" className="rounded-xl border border-border p-4 text-small text-ink-muted">
          Carregando a tabela de preço do item…
        </p>
      )}

      {podeRegistrar && modoTabela.modo !== 'CARREGANDO' && (
        <form onSubmit={(e) => void vig.handleSubmit(enviar)(e)} noValidate aria-label="Nova vigência de tributos" className="rounded-xl border border-border p-4">
          <h3 className="text-body font-semibold">Nova vigência</h3>
          {estruturada && (
            <p className="mt-1 text-small text-ink-secondary">
              Tabela padrão{modoTabela.tabelaNome ? ` "${modoTabela.tabelaNome}"` : ''} em modo estruturado. Campo vazio usa a tabela; preenchido vira exceção deste item.
            </p>
          )}
          <div className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-3.5">
            <div>
              <Label htmlFor="v-data" className={ROTULO}>
                A partir de
              </Label>
              <Input id="v-data" type="date" aria-invalid={errors.validFrom ? true : undefined} aria-describedby={errors.validFrom ? 'v-data-erro' : undefined} {...vig.register('validFrom')} />
              <MensagemCampo id="v-data-erro" mensagem={errors.validFrom?.message} />
            </div>
            {estruturada ? (
              <CamposExcecaoTributos form={vig} perfil={modoTabela.perfil} />
            ) : (
              CAMPOS_TAXA.map(({ campo, rotulo }) => (
                <div key={campo}>
                  <Label htmlFor={`v-${campo}`} className={ROTULO}>
                    {rotulo} (%)
                  </Label>
                  <Input
                    id={`v-${campo}`}
                    inputMode="decimal"
                    className="font-mono"
                    placeholder="0"
                    aria-invalid={errors[campo] ? true : undefined}
                    aria-describedby={errors[campo] ? `v-${campo}-erro` : undefined}
                    {...vig.register(campo)}
                  />
                  <MensagemCampo id={`v-${campo}-erro`} mensagem={errors[campo]?.message} />
                </div>
              ))
            )}
            <div>
              <Label htmlFor="v-cst" className={ROTULO}>
                CST ICMS
              </Label>
              <select id="v-cst" className={CAMPO_SELECT} aria-invalid={errors.cstIcms ? true : undefined} aria-describedby={errors.cstIcms ? 'v-cst-erro' : undefined} {...vig.register('cstIcms')}>
                <option value="">{OPCAO_VAZIA}</option>
                {[...CST_ICMS, ...CSOSN_ICMS].map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <MensagemCampo id="v-cst-erro" mensagem={errors.cstIcms?.message} />
            </div>
            <div className="flex items-end">
              <label htmlFor="v-icms-st" className="flex h-10 cursor-pointer items-center gap-2">
                <input id="v-icms-st" type="checkbox" className="size-[18px] accent-primary" {...vig.register('icmsSt')} />
                <span>ICMS-ST (substituição tributária)</span>
              </label>
            </div>
          </div>

          <fieldset className="mt-4 rounded-xl border border-dashed border-border p-3.5">
            <legend className="px-1 text-small font-semibold">Cadastro fiscal (só cadastro)</legend>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
              <CampoMascarado form={vig} nome="ncm" rotulo="NCM" grupos={GRUPOS.ncm} placeholder="0000.00.00" />
              <CampoMascarado form={vig} nome="cest" rotulo="CEST" grupos={GRUPOS.cest} placeholder="00.000.00" />
              <CampoSelect form={vig} nome="cstPis" rotulo="CST PIS" opcoes={CST_PIS_COFINS} />
              <CampoSelect form={vig} nome="cstCofins" rotulo="CST COFINS" opcoes={CST_PIS_COFINS} />
              <CampoSelect form={vig} nome="cstIpi" rotulo="CST IPI" opcoes={CST_IPI} />
              {tipo === 'SERVICO' && <CampoMascarado form={vig} nome="nbs" rotulo="NBS" grupos={GRUPOS.nbs} placeholder="0.0000.00.00" />}
            </div>
          </fieldset>

          {estruturada && <RegrasEstruturadas form={vig} />}

          <fieldset className="mt-4 rounded-xl border border-dashed border-border p-3.5">
            <legend className="px-1 text-small font-semibold">Reforma tributária (IBS, CBS e Imposto Seletivo)</legend>
            <p role="note" className="mb-3 rounded-[10px] bg-surface-muted px-3 py-2 text-small text-ink-secondary">
              {AVISO_REFORMA}
            </p>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
              <CampoApenasDigitos form={vig} nome="cstIbsCbs" rotulo="CST IBS/CBS" tamanho={3} />
              <CampoApenasDigitos form={vig} nome="cClassTrib" rotulo="cClassTrib" tamanho={6} />
              <CampoTaxa form={vig} nome="reducaoIbs" rotulo="Redução IBS (%)" />
              <CampoTaxa form={vig} nome="reducaoCbs" rotulo="Redução CBS (%)" />
              <div className="flex items-end">
                <label htmlFor="v-is" className="flex h-10 cursor-pointer items-center gap-2">
                  <input id="v-is" type="checkbox" className="size-[18px] accent-primary" {...vig.register('impostoSeletivo')} />
                  <span>Sujeito ao Imposto Seletivo</span>
                </label>
              </div>
              {vig.watch('impostoSeletivo') && <CampoTaxa form={vig} nome="aliquotaIs" rotulo="Alíquota do Imposto Seletivo (%)" />}
            </div>
          </fieldset>

          <div className="col-span-full mt-3.5">
            <Label htmlFor="v-motivo" className={ROTULO}>
              Motivo (opcional)
            </Label>
            <Input id="v-motivo" maxLength={300} placeholder="Ex.: convênio, mudança de NCM" {...vig.register('motivo')} />
            <MensagemCampo id="v-motivo-erro" mensagem={errors.motivo?.message} />
          </div>
          <p className="mt-3 rounded-[10px] bg-surface-muted px-3 py-2.5 text-small text-ink-secondary">
            Alíquotas com vigência: uma mudança cria nova versão a partir da data informada e encerra a anterior nessa data; nunca sobrescreve. Formações e
            orçamentos antigos mantêm a alíquota da época.{' '}
            {estruturada ? 'Campo de alíquota vazio usa a tabela; não é zero.' : 'Campo vazio vale 0%.'}
          </p>
          {erro !== null && (
            <div className="mt-3">
              <ErroFormacao erro={erro} titulo="Não foi possível registrar a vigência" />
            </div>
          )}
          {ok && (
            <p role="status" className="mt-3 text-small text-success-ink">
              {ok}
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <button
              type="submit"
              disabled={registrar.isPending}
              className="h-9 rounded-[10px] bg-primary px-3.5 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {registrar.isPending ? 'Registrando…' : 'Registrar vigência'}
            </button>
            {isDirty && (
              <button type="button" onClick={() => vig.reset(padrao)} className="h-9 rounded-[10px] border border-border bg-card px-3.5 text-small hover:bg-line-soft">
                Descartar
              </button>
            )}
          </div>
        </form>
      )}

      <div>
        <h3 className="text-body font-semibold">Histórico de vigências</h3>
        {historico.isError ? (
          <div className="mt-2">
            <ErroFormacao erro={historico.error} onTentar={() => void historico.refetch()} />
          </div>
        ) : !historico.data ? (
          <p role="status" className="mt-2 text-small text-ink-muted">
            Carregando histórico…
          </p>
        ) : historico.data.length === 0 ? (
          <p role="status" className="mt-2 text-small text-ink-muted">
            Nenhuma vigência registrada ainda.
          </p>
        ) : (
          <div className="mt-2 overflow-x-auto">
            <table data-testid="historico-tributos" className="w-full min-w-[640px] border-collapse text-small">
              <thead>
                <tr className="text-left text-caption text-ink-muted">
                  <th scope="col" className="py-2 pr-3 font-medium">Vigência</th>
                  {CAMPOS_TAXA.map(({ campo, rotulo }) => (
                    <th key={campo} scope="col" className="px-3 py-2 text-right font-medium">{rotulo}</th>
                  ))}
                  <th scope="col" className="px-3 py-2 font-medium">CST</th>
                  <th scope="col" className="px-3 py-2 font-medium">NCM</th>
                  <th scope="col" className="px-3 py-2 font-medium">Motivo</th>
                </tr>
              </thead>
              <tbody>
                {historico.data.map((t) => (
                  <tr key={t.id} data-testid="vigencia-tributo" className="border-t border-line-soft">
                    <td className="tabular py-2.5 pr-3">
                      {formatarData(t.validFrom)} {t.validTo ? `– ${formatarData(t.validTo)}` : <span className="rounded-full bg-success-bg px-2 py-0.5 text-caption font-semibold text-success-ink">vigente</span>}
                    </td>
                    {CAMPOS_TAXA.map(({ campo }) => (
                      <td key={campo} className="tabular px-3 py-2.5 text-right">{formatarTaxa(t[campo])}</td>
                    ))}
                    <td className="tabular px-3 py-2.5">{t.cstIcms ?? '—'}</td>
                    <td className="tabular px-3 py-2.5">{t.ncm ? mascaraFiscal(t.ncm, GRUPOS.ncm) : '—'}</td>
                    <td className="px-3 py-2.5 text-ink-secondary">{t.motivo ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const ROTULO_COLUNA: Record<CampoColunaAntiga, string> = { pis: 'PIS', cofins: 'COFINS', icms: 'ICMS', iss: 'ISS' };

/**
 * Sessao 51b (U8): colunas antigas com valor que a tabela estruturada nao usa (campo de excecao vazio). "Converter em exceção"
 * grava o valor como excecao deste item (so com tributo.editar); ICMS fica em botao proprio, pois valeria em todas as UFs.
 * Montado enquanto a aba esta em modo estruturado: o resultado da conversao continua visivel quando o aviso some.
 */
function AvisoColunasAntigas({ itemId, vigente, podeConverter }: { itemId: string; vigente: ItemTributoResponse | null | undefined; podeConverter: boolean }) {
  const converter = useConverterExcecao();
  const [resultado, setResultado] = useState<ConverterExcecaoResponse | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const pendentes = colunasAntigasPendentes(vigente);
  const principais = pendentes.filter((c) => c !== 'icms');
  const temIcms = pendentes.includes('icms');

  if (pendentes.length === 0 && resultado === null && erro === null) return null;

  async function converterEm(campos: CampoColunaAntiga[]) {
    setErro(null);
    setResultado(null);
    try {
      setResultado(await converter.mutateAsync({ itemIds: [itemId], campos }));
    } catch (e) {
      setErro(e);
    }
  }

  const botao = 'h-9 rounded-[10px] border border-border bg-card px-3.5 text-small font-semibold hover:bg-line-soft disabled:opacity-50';

  return (
    <section aria-label="Colunas antigas do item" className="rounded-xl border border-border p-4 text-small">
      {pendentes.length > 0 && (
        <>
          <p role="note" className="text-ink-body">
            Estes valores não entram no cálculo da tabela estruturada (exceto IPI). Para manter uma alíquota diferente, cadastre como exceção.
          </p>
          <p className="mt-1 text-caption text-ink-secondary">{pendentes.map((c) => `${ROTULO_COLUNA[c]} ${formatarTaxa(vigente?.[c])}`).join(' · ')}</p>
          {podeConverter && principais.length > 0 && (
            <button type="button" className={`${botao} mt-3`} disabled={converter.isPending} onClick={() => void converterEm(principais)}>
              Converter em exceção
            </button>
          )}
          {podeConverter && temIcms && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <button type="button" className={botao} disabled={converter.isPending} onClick={() => void converterEm(['icms'])}>
                Converter ICMS em exceção
              </button>
              <span className="text-caption text-ink-secondary">O ICMS do item valeria em todas as UFs.</span>
            </div>
          )}
        </>
      )}
      {erro !== null && (
        <div className="mt-3">
          <ErroFormacao erro={erro} titulo="Não foi possível converter em exceção" />
        </div>
      )}
      {resultado && (
        <p role="status" className="mt-3 text-success-ink">
          {`Convertidos: ${formatarContagem(resultado.convertidos)}. Ignorados: ${formatarContagem(resultado.ignorados)}.`}
        </p>
      )}
    </section>
  );
}

function Fiscal({ rotulo, valor }: { rotulo: string; valor: string | null }) {
  return (
    <div>
      <dt className="text-caption text-ink-muted">{rotulo}</dt>
      <dd className="tabular font-medium">{valor ?? '—'}</dd>
    </div>
  );
}

/** Campo com mascara de pontuacao (NCM, CEST, NBS). A mascara so reescreve; o contrato valida o numero de digitos. */
function CampoMascarado({ form, nome, rotulo, grupos, placeholder }: { form: Vig; nome: 'ncm' | 'cest' | 'nbs'; rotulo: string; grupos: readonly number[]; placeholder: string }) {
  const id = `v-${nome}`;
  const erro = form.formState.errors[nome]?.message;
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <Controller
        control={form.control}
        name={nome}
        render={({ field }) => (
          <Input
            id={id}
            inputMode="numeric"
            className="font-mono"
            placeholder={placeholder}
            value={field.value ?? ''}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(mascaraFiscal(e.target.value, grupos))}
            aria-invalid={erro ? true : undefined}
            aria-describedby={erro ? `${id}-erro` : undefined}
          />
        )}
      />
      <MensagemCampo id={`${id}-erro`} mensagem={erro} />
    </div>
  );
}

/** Codigo so de digitos com tamanho fixo (CST IBS/CBS = 3, cClassTrib = 6). */
function CampoApenasDigitos({ form, nome, rotulo, tamanho }: { form: Vig; nome: 'cstIbsCbs' | 'cClassTrib'; rotulo: string; tamanho: number }) {
  const id = `v-${nome}`;
  const erro = form.formState.errors[nome]?.message;
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <Controller
        control={form.control}
        name={nome}
        render={({ field }) => (
          <Input
            id={id}
            inputMode="numeric"
            className="font-mono"
            maxLength={tamanho}
            value={field.value ?? ''}
            onBlur={field.onBlur}
            onChange={(e) => field.onChange(apenasDigitos(e.target.value, tamanho))}
            aria-invalid={erro ? true : undefined}
            aria-describedby={erro ? `${id}-erro` : undefined}
          />
        )}
      />
      <MensagemCampo id={`${id}-erro`} mensagem={erro} />
    </div>
  );
}

/** CST em lista oficial (contrato). Opcao vazia = nao informado. */
function CampoSelect({ form, nome, rotulo, opcoes }: { form: Vig; nome: 'cstPis' | 'cstCofins' | 'cstIpi'; rotulo: string; opcoes: readonly string[] }) {
  const id = `v-${nome}`;
  const erro = form.formState.errors[nome]?.message;
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <select id={id} className={CAMPO_SELECT} aria-invalid={erro ? true : undefined} aria-describedby={erro ? `${id}-erro` : undefined} {...form.register(nome)}>
        <option value="">{OPCAO_VAZIA}</option>
        {opcoes.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
      <MensagemCampo id={`${id}-erro`} mensagem={erro} />
    </div>
  );
}

/** Percentual de reducao ou aliquota (digitado em %; o contrato converte em fracao e recusa acima de 100%). */
function CampoTaxa({ form, nome, rotulo }: { form: Vig; nome: 'reducaoIbs' | 'reducaoCbs' | 'aliquotaIs'; rotulo: string }) {
  const id = `v-${nome}`;
  const erro = form.formState.errors[nome]?.message;
  return (
    <div>
      <Label htmlFor={id} className={ROTULO}>
        {rotulo}
      </Label>
      <Input id={id} inputMode="decimal" className="font-mono" placeholder="0" aria-invalid={erro ? true : undefined} aria-describedby={erro ? `${id}-erro` : undefined} {...form.register(nome)} />
      <MensagemCampo id={`${id}-erro`} mensagem={erro} />
    </div>
  );
}
